import { useState } from 'react';
import type { CSSProperties, InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** Style applied to the <input> itself (paddingRight is added so text never
   *  runs under the toggle). */
  inputStyle?: CSSProperties;
  /** Style applied to the positioning wrapper (e.g. to carry a marginBottom
   *  that previously lived on the input). */
  wrapperStyle?: CSSProperties;
  /** Eyeball color — override on dark backgrounds. */
  toggleColor?: string;
};

/**
 * Password / passphrase input with a show/hide eyeball toggle.
 * Manages its own reveal state; spread any standard input props through.
 */
export default function PasswordRevealInput({
  inputStyle,
  wrapperStyle,
  toggleColor = 'var(--text-muted)',
  ...inputProps
}: Props) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', ...wrapperStyle }}>
      <input
        {...inputProps}
        type={show ? 'text' : 'password'}
        style={{ ...inputStyle, paddingRight: '2.75rem' }}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        aria-label={show ? 'Hide password' : 'Show password'}
        title={show ? 'Hide' : 'Show'}
        tabIndex={-1}
        style={{
          position: 'absolute',
          right: '0.6rem',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'none',
          border: 'none',
          padding: 0,
          color: toggleColor,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          lineHeight: 0,
        }}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}
